"use client";

import React, { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

// ============================================================================
// TYPES
// ============================================================================

interface SimpleTableMobileSkeletonProps {
  count?: number;
  showActions?: boolean;
}

// ============================================================================
// MOBILE SKELETON COMPONENT
// ============================================================================

export function SimpleTableMobileSkeleton({
  count = 5,
  showActions = true,
}: SimpleTableMobileSkeletonProps) {
  // Memoize skeleton items to prevent recreation on every render
  const skeletonItems = useMemo(
    () =>
      Array.from({ length: count }, (_, index) => (
        <Card key={index} className="mb-3 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              {/* Left content */}
              <div className="flex-1 min-w-0 mr-3">
                {/* Primary field (title/name) */}
                <Skeleton className="h-5 w-3/4 mb-2" />

                {/* Secondary field */}
                <Skeleton className="h-4 w-1/2 mb-3" />

                {/* Tags/badges row */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>

                {/* Bottom row - metadata */}
                <div className="flex items-center gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>

              {/* Right side - actions or avatar */}
              <div className="flex flex-col items-end gap-2">
                {showActions && (
                  <>
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-4 w-12" />
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )),
    [count, showActions]
  );

  return <div className="space-y-0">{skeletonItems}</div>;
}

// Memoize the component for performance
export const SimpleTableMobileSkeletonMemo = React.memo(SimpleTableMobileSkeleton);
